<template>
  <LoadingSpinner v-if="isLoading" />
  <div v-else class="create-budget">
    <ProjectHeader :project="project" />
    <ProjectSubNavigation :project="project" />

    <div class="mb-8">
      <BudgetAnalytics
        :materials-budget="materialBudget"
        :labors-budget="laborBudget"
        :total-budget-expenses="totalBudgetExpenses"
        :total-budget-revenue="totalBudgetRevenue"
      />
    </div>

    <!-- edit budget -->
    <div class="mt-8">
      <form.Field
        name="categories"
      >
        <template #default="{ field: categoriesField }">
          <div>
            <BaseDraggable
              :model-value="(categoriesField.state.value as any[])"
              item-key="key"
              items-id-prefix="key"
              @update:model-value="(values) => setCategories(values, categoriesField)"
            >
              <template #delete-button="{ element, index }">
                <span
                  class="i-heroicons-trash text-error"
                  :class="categoriesField.form.getFieldValue('categories').length === 1 ? 'text-gray-400 cursor-not-allowed' : 'text-error cursor-pointer'"
                  :disabled="categoriesField.form.getFieldValue('categories').length === 1"
                  @click="categoriesField.form.getFieldValue('categories').length === 1 ? '' : deleteCategory(filterBudgetItemByCategory(element), index, categoriesField)"
                />
              </template>

              <template #collapsed-text="{ element }">
                <div class="flex item-center py-2">
                  <span class="font-bold">{{ element.name }}</span>
                </div>
              </template>

              <template
                #collapse-button="{ open: openCat, element, toggleFn: toggleFnCat }"
              >
                <span
                  class="text-gray-600 size-4"
                  :class="
                    openCat
                      ? 'i-mdi-unfold-less-horizontal'
                      : 'i-mdi-unfold-more-horizontal'
                  "
                  @click="toggleFnCat(element.id)"
                />
              </template>

              <template #item-content="{ element: category, index: categoryIndex, open: openCat }">
                <div :id="`key${categoryIndex}`">
                  <div>
                    <form.Field
                      :key="`field-${categoryIndex}`"
                      :name="`categories[${categoryIndex}].name`"
                      :validators="{
                        onChangeListenTo: ['categories'],
                        onChange: yup.string().required('Category is required')
                      }"
                    >
                      <template #default="{ field }">
                        <BaseInput
                          :name="field.name"
                          :model-value="category.name"
                          placeholder="Category"
                          class="mt-4"
                          :error="(field.state.meta.errors && field.state.meta.errors[0] as string)"
                          @blur="updateBudgetItemsCategory($event, category.name, categoryIndex, field)"
                        />
                      </template>
                    </form.Field>
                  </div>

                  <!-- line items -->
                  <BaseDraggable
                    :key="`line-items-${categoryIndex}`"
                    :model-value="sortedLineItems(category)"
                    item-key="id"
                    :items-id-prefix="`id${category.key}`"
                    @update:model-value="reOrderItems"
                    @delete="(event) => deleteItem(event.element.id)"
                  >
                    <template #delete-button="{ element }">
                      <span
                        class="i-heroicons-trash text-error"
                        @click="deleteItem(element.id)"
                      />
                    </template>
                    <template #collapse-button="{ open, element, toggleFn }">
                      <span
                        class="text-gray-600 size-4"
                        :class="
                          open
                            ? 'i-mdi-unfold-less-horizontal'
                            : 'i-mdi-unfold-more-horizontal'
                        "
                        @click="toggleFn(element.id)"
                      />
                    </template>

                    <template #collapsed-text="{ element }">
                      <div class="flex item-center py-2">
                        <span class="font-bold">{{ element.value.title }}</span>
                      </div>
                    </template>

                    <template #item-content="{ element: item, index, open }">
                      <div :id="`id${category.key}${index}`">
                        <CreateBudgetItem
                          :id="item.value.id"
                          :key="`item-${category.id}-${item.weight}`"
                          v-model="item.value"
                          :is-saving="false"
                          :is-open="open && openCat"
                          @can-submit="(value) => itemsValidations[item.value.id]=value"
                          @on-delete="deleteItem(item.value.id)"
                        />
                      </div>
                    </template>
                  </BaseDraggable>

                  <hr class="my-6" />

                  <div class="grid grid-cols-2 gap-4">
                    <div>
                      <BaseButton
                        variant="base"
                        type="button"
                        class="w-full"
                        style="box-shadow: none !important"
                        @click="addBudgetItem(categoryIndex, category)"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <span class="i-heroicons-plus-circle size-8" />
                          <span class="text-lg">Add line item</span>
                        </div>
                      </BaseButton>
                    </div>

                    <div>
                      <BaseButton
                        data-modal="v-b-modal.budgetTemplateModal"
                        variant="base"
                        type="button"
                        class="w-full"
                        style="box-shadow: none !important"
                        @click.prevent="showTemplatesModal(category)"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <span class="i-heroicons-plus-circle size-8" />
                          <span class="text-lg">Add from template</span>
                        </div>
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </template>
            </BaseDraggable>

            <div class="mt-4">
              <BaseButton
                variant="base"
                type="button"
                class="w-full"
                style="box-shadow: none !important"
                @click.prevent="addBudgetItemCategory(categoriesField)"
              >
                <span class="i-heroicons-plus-circle size-8" />
                <br />
                <p class="text-lg">Add category</p>
              </BaseButton>
            </div>
          </div>
        </template>
      </form.Field>
    </div>

    <hr class="my-4" />
    <form.Subscribe>
      <template #default="{ canSubmit, isSubmitting }">
        <div class="grid items-center grid-cols-2 gap-4">
          <!-- last auto-saving indicator -->
          <div class="text-sm text-gray-500">
            <div
              v-if="isAutoSaveEnabled"
              class="flex items-center gap-2"
            >
              <span>Last auto-saved at:</span>
              <span v-if="!autoSaving" class="font-bold">
                {{ lastAutoSave ? lastAutoSave.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A' }}
              </span>
              <span v-if="autoSaving" class="i-heroicons-ellipsis-horizontal animate-pulse font-bold size-6 inline-block" />
            </div>

            <div v-else class="text-gray-500 font-bold">
              Auto save is disabled
            </div>
          </div>

          <div class="flex items-center justify-end gap-2 flex-1">
            <BaseButton
              :disabled="isSubmitting"
              text="Cancel"
              :class="{
                'cursor-not-allowed opacity-50':isSubmitting,
              }"
              @click="cancel"
            />

            <BaseButton
              :disabled="
                !canSubmit ||
                !isBudgetItemsFormsValid ||
                isSubmitting ||
                !allCategoriesHaveItems
              "
              text="Save"
              type="submit"
              variant="primary"
              :class="{
                'cursor-not-allowed opacity-50':
                  !canSubmit ||
                  isSubmitting ||
                  !isBudgetItemsFormsValid ||
                  !allCategoriesHaveItems
              }"
              @click="form.handleSubmit()"
            />
          </div>
        </div>
      </template>
    </form.Subscribe>
  </div>

  <Modal
    id="budgetTemplateModal"
    v-model="showModal"
    title="Apply Budget Template"
    :no-escape="true"
    :no-backdrop="true"
    :no-close-button="true"
    width="w-full md:w-[80%] lg:w-[70%] xl:w-[60%]"
  >
    <template #body>
      <BudgetItemTemplates
        @on-buget-template-selected="bugetTemplateSelected"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import { Budget, BudgetItem } from '@/models/budget'
import { filter, omit, uniqueId } from 'underscore'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CreateBudgetItem from '../components/CreateBudgetItem.vue'
import BudgetAnalytics from '../components/BudgetAnalytics.vue'
import BudgetItemTemplates from '../components/BudgetItemTemplates.vue'
import ProjectSubNavigation from '@/components/project/ProjectSubNavigation.vue'
import ProjectHeader from '@/components/project/ProjectHeader.vue'
import { useHead } from '@unhead/vue'
import { useRoute, useRouter } from 'vue-router'
import BudgetService from '@/services/BudgetService'
import { useNotification } from '@kyvg/vue3-notification'
import { useMutation, useQuery } from '@tanstack/vue-query'
import ProjectService from '@/services/ProjectService'
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { toNumber } from '@/utils'
import BaseDraggable from '@/components/base/BaseDraggable.vue'
import { useForm } from '@tanstack/vue-form'
import { yupValidator } from '@tanstack/yup-form-adapter'
import * as yup from 'yup'
import Modal from '@/components/app/AppModal.vue'
import { PROJECT_STATUS } from '@/models/project'
import SettingService from '@/services/SettingService'
import { watch } from 'vue'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'
import { syncLineItemIds } from '@/utils/syncLineItemIds'

useHead({ title: 'Create new budget' })

const projectId = useRoute().params.id as string
const $router = useRouter()
const budgetService = new BudgetService()
const budget = ref(new Budget())

const deletedItems = ref<string[]>([])
const itemsValidations = ref({})
const budgetTemplateCategory = ref(null)
const showModal = ref(false)
const { notify } = useNotification()
const hasUnsavedChanges = ref<boolean|null>(true)

const { data: project, isPending: isLoadingProject } = useQuery({
  queryKey: ['project', projectId],
  queryFn: () => new ProjectService().find(projectId),
  enabled: !!projectId,
})

const isLoading = computed(() => isLoadingProject.value || isLoadingGeneralSettings.value)

const isBudgetItemsFormsValid = computed(() => {
  return Object.values(itemsValidations.value).every((value) => value)
})

// categories functions
const setCategories = (value, categoriesField) => {
  const newCategories = value.map((category, index) => {
    const newCategoryWeight = index + 1
    const newCategory = {
      ...category,
      key: `${category.name}-${newCategoryWeight}`.replace(/\s/g, '_'),
      weight: newCategoryWeight
    }
    budget.value.lineItems.forEach((item) => {
      if (item.category.name === newCategory.name && item.category.weight === category.weight) {
        item.category.weight = newCategory.weight
      }
    })
    return newCategory
  })
  categoriesField.handleChange(newCategories)
}

const deleteCategory = (items, index, categoriesField) => {
  items.forEach(item => {
    deleteItem(item.id)
  })
  categoriesField.removeValue(index)
}

const updateBudgetItemsCategory = ($event, oldCategoryName, index, field) => {
  budget.value.lineItems.forEach((item) => {
    if (item.category.name === oldCategoryName) {
      item.category.name = $event.target.value
    }
  })
  field.handleChange($event.target.value)
}


const defaultCategoryName = () => {
  const name = 'Untitled Category'
  const categoriesNames = categories.value.map((category) => category.name)
  let i = 1
  let newName = name

  while (categoriesNames.includes(newName)) {
    newName = `${name} ${i}`
    i++
  }

  return newName
}

// find the highest weight and add 1
const nextCategoryWeight = () => {
  return (
    Math.max(...categories.value.map((category) => category.weight)) + 1
  )
}

const addBudgetItemCategory = (categoriesField) => {
  const category = {
    name: defaultCategoryName(),
    weight: nextCategoryWeight(),
    key: `${defaultCategoryName()}-${nextCategoryWeight()}`.replace(/\s/g, '_'),
    id: uniqueId()
  }
  categoriesField.pushValue(category)
}


// form functions
const defaultValues = reactive({
  categories: []
})

defaultValues.categories = [{
  name: 'Untitled Category',
  weight: 1,
  key: 'Untitled Category-1'.replace(/\s/g, '_'),
  id: uniqueId()
}]

const form = useForm({
  defaultValues,
  validatorAdapter: yupValidator(),
  onSubmit: async () => {
    const budgetToCreate = {
      ...budget.value,
      projectId: project.value.id,
      lineItems: budget.value.lineItems.map((item) => {
        return {
          ...item,
          category: {
            name: item.category.name,
            weight: item.category.weight
          }
        }
      })
    }
    await saveBudget(budgetToCreate)
  },
})

const categories = form.useStore((state) => state.values.categories)
const allCategoriesHaveItems = computed(() => {
  return categories.value.every((category) => {
    return budget.value.lineItems.some((item) => item.category.name === category.name && item.category.weight === category.weight)
  })
})
// line items functions
const filterBudgetItemByCategory = computed(() => (category) => {
  if (!budget.value.lineItems) return []
  return filter(budget.value.lineItems, (item) => {
    return (
      item.category.name === category.name &&
      item.category.weight === category.weight
    )
  }).sort((a, b) => a.weight - b.weight)
})



const deleteItem = (itemId) => {
  const index = budget.value.lineItems.findIndex((item) => item.id === itemId)

  if (index > -1) {
    budget.value.lineItems.splice(index, 1)
    itemsValidations.value = omit(itemsValidations.value, itemId)
    deletedItems.value.push(itemId)
  }
}

const sortedLineItems = computed(() => (category) => {
  return filterBudgetItemByCategory.value(category).map((item) => {
    return {
      value: item,
      id: item.id,
    }
  })
})

const reOrderItems = (newItems) => {
  newItems.forEach((item, index) => {
    const budgetItem = budget.value.lineItems.find(
      (lineItem) => lineItem.id === item.value.id,
    )
    budgetItem.weight = index + 1
  })
}

const nextLineItemWeight = (category) => {
  const items = budget.value.lineItems.filter(
    (item) =>
      item.category.name === category.name &&
      item.category.weight === category.weight,
  )
  return items.length ? Math.max(...items.map((item) => item.weight)) + 1 : 1
}

const addBudgetItem = (categoryIndex, category) => {
  const budgetItem = new BudgetItem()
  budgetItem.id = `${new Date().getTime()}-generatedID`
  budgetItem.materialsBudget = 0
  budgetItem.laborBudget = 0
  budgetItem.category = category
  budgetItem.weight = nextLineItemWeight(category)
  budget.value.lineItems.push(Object.assign({}, budgetItem))
}

const showTemplatesModal = (category) => {
  budgetTemplateCategory.value = category
  showModal.value = true
}

const bugetTemplateSelected = (items: BudgetItem[]) => {
  items.forEach((item) => {
    const budgetItem = Object.assign({}, JSON.parse(JSON.stringify(item)))

    if ('_highlightResult' in budgetItem) {
      delete budgetItem._highlightResult
    }

    budgetItem.id = `${new Date().getTime() + uniqueId()}-generatedID`
    budgetItem.category = budgetTemplateCategory.value
    budgetItem.weight = nextLineItemWeight(budgetTemplateCategory.value)
    budget.value.lineItems.push(budgetItem)
  })
  showModal.value = false
}

const cancel = () => {
  $router.push({
    name: 'ShowProject',
    params: {
      customerId: project.value.customerId,
      projectId: project.value.id
    }
  })
}

// materials and labor functions
const materialBudget = computed(() => {
  let materialCost = 0
  budget.value.lineItems.map((item) => {
    if (!item.isCreditLineItem) {
      materialCost += toNumber(item.materialsBudget)
    }
  })

  return isNaN(materialCost) ? 0 : materialCost
})

const laborBudget = computed(() => {
  let laborCost = 0
  budget.value.lineItems.map((item) => {
    if (!item.isCreditLineItem) {
      laborCost += toNumber(item.laborBudget)
    }
  })
  return isNaN(laborCost) ? 0 : laborCost
})

const totalBudgetExpenses = computed(() => {
  let expenses = 0
  budget.value.lineItems.map((item) => {
    if (!item.isCreditLineItem) {
      expenses += toNumber(item.cost)
    }
  })
  return expenses
})

const totalBudgetRevenue = computed(() => {
  let revenue = 0
  budget.value.lineItems.map((item) => {
    revenue += toNumber(item.totalPrice)
  })
  return revenue
})

const { mutateAsync: saveBudget } = useMutation({
  mutationFn: (budget: Budget) => {
    if (budget.id) {
      return budgetService.updateBudget(budget, deletedItems.value)
    } else {
      return budgetService.createBudget({...budget, id: project.value.id } as Budget)
    }
  },
  onSuccess: (createdBudget: Budget) => {
    notify({
      type: 'success',
      title: 'Budget created',
      text: 'Budget created successfully'
    })
    hasUnsavedChanges.value = false
    $router.push({ name: 'ShowBudget', params: { id: createdBudget.id } })
  },
  onError: (error) => {
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while trying to create the budget'
    })
  }
})


// auto save functionality
const { data:generalSettings, isLoading:isLoadingGeneralSettings } = useQuery({
  queryKey: ['settings', 'generalSettings'],
  queryFn: async () => {
    const settings = await new SettingService().find('generalSettings')
    return settings
  }
})

let autoSaveTimeout: ReturnType<typeof setInterval>
const autoSaving = ref(false)
const lastAutoSave = ref<Date | null>(null)
const isAutoSaveEnabled = ref(false)

watch([() => project.value, () => generalSettings.value], ([proj, settings]) => {
  if (!proj || !settings) return
  if (project.value && project.value.status == PROJECT_STATUS.ESTIMATING) {
    isAutoSaveEnabled.value = Object.hasOwn(generalSettings.value, 'enableBudgetAutoSave') ? generalSettings.value.enableBudgetAutoSave : true
    const waitTime = generalSettings.value.autoSaveWaitTime || 10

    if (!isAutoSaveEnabled.value) {
      return
    }

    clearInterval(autoSaveTimeout)
    autoSaveTimeout = setInterval(async () => {
      if (categories.value.length > 0) {
        autoSaving.value = true
        lastAutoSave.value = null
        try {
          let savedBudget = null
          if (budget.value.id) {
            savedBudget = await budgetService.updateBudget({
              ...budget.value,
              lineItems: budget.value.lineItems.map((item) => {
                return {
                  ...item,
                  category: {
                    name: item.category.name,
                    weight: item.category.weight
                  }
                }
              })
            }, deletedItems.value)
          } else {
            savedBudget = await budgetService.createBudget({
              ...budget.value,
              id: project.value.id,
              projectId: project.value.id,
              lineItems: budget.value.lineItems.map((item) => {
                return {
                  ...item,
                  category: {
                    name: item.category.name,
                    weight: item.category.weight
                  }
                }
              })
            })
          }

          // update the budget.lineItems ids
          budget.value.id = savedBudget.id
          const localItems = budget.value.lineItems
          const updatedLineItems = syncLineItemIds(localItems, savedBudget.lineItems)
          budget.value.lineItems = updatedLineItems

          lastAutoSave.value = new Date()
        } catch (error) {
          console.error('Auto save error:', error)
          notify({
            type: 'error',
            title: 'Auto save error',
            text: error.message || 'An error occurred while auto saving the budget'
          })
        } finally {
          autoSaving.value = false
        }
      }
    }, waitTime * 1000)
  } else {
    clearInterval(autoSaveTimeout)
  }
}, { immediate: true })


onBeforeUnmount(() => {
  clearInterval(autoSaveTimeout)
})

// prevent user from navigating away without saving
useUnsavedChangesWarning(hasUnsavedChanges)
</script>

<style scoped>
.footer-btns {
  display: flex;
  flex-direction: row;
}
</style>
